import fetch from "isomorphic-fetch";
import { routes, apiEndpoints } from "@common/constants/urls";
import mqttService from "../../monitor/mqttService";
import analyticsService, {
  ANALYTICS_TYPES,
} from "@common/services/analyticsService";
import { getToken, getExp } from "@common/services/authStorage";

let refreshPromise = null;

const defaultHeaders = {
  "Content-Type": "application/json",
  client: "Breweryportal",
  version: "1.73.51" || 0,
};

async function handleResponse(res) {
  const contentType = res.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    // When the body's empty Backend should probably send a 204 instead
    if (res.status === 401) invalidToken();
    const data = await res.json();
    if (res.status >= 400) throw data;
    return data;
  } else if (!res.ok) {
    throw new Error(res.statusText);
  }
  return null;
}

function invalidToken() {
  localStorage.removeItem("token");
  localStorage.removeItem("userId");
  localStorage.removeItem("exp");
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("userId");
  sessionStorage.removeItem("exp");
  sessionStorage.removeItem("is_impersonating");

  // If already at sign in page, we don't have to route
  if (location.href.indexOf(routes.signin) === -1) {
    analyticsService.track(ANALYTICS_TYPES.SIGN_OUT, { Forced: true });
    analyticsService.mixpanel.reset();
    location.href = "/";
  }
}

async function refreshAccessToken() {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "TOKEN " + "4c433da015985d17669c604a5a4e2c906083e815",
    client: "Breweryportal",
    version: "1.73.51" || 0,
  };

  const res = await fetch("https://api.minibrew.io/" + apiEndpoints.refreshToken, {
    method: "POST",
    headers,
    body: JSON.stringify({ refresh: getToken() }),
  });

  const data = await handleResponse(res);
  // Normalize timestamp -> /token/ returns in ms instead of s
  const expiration = data.exp * 1000;

  if (sessionStorage.getItem("is_impersonating")) {
    sessionStorage.setItem("token", data.token);
    sessionStorage.setItem("exp", expiration);
  } else {
    localStorage.setItem("token", data.token);
    localStorage.setItem("exp", expiration);
  }

  mqttService.reconnect(data.token);
  return data.token;
}

async function getAuthHeaders(customHeaders) {
  const currentTime = new Date().getTime();
  const expTime = getExp();

  if (expTime > currentTime) {
    return { ...customHeaders, Authorization: "Bearer " + getToken() };
  }

  // Deduplicate concurrent refresh requests with a shared promise
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }

  const newToken = await refreshPromise;
  return { ...customHeaders, Authorization: "Bearer " + newToken };
}

function prepareHeaders(headers) {
  const prepared = { ...headers };
  // If type is multipart, remove the whole header to allow automatization
  // of config and boundary directly from browser
  if (prepared["Content-Type"]?.indexOf("multipart/form-data") > -1) {
    delete prepared["Content-Type"];
  }
  return prepared;
}

function serializeBody(headers, body) {
  return headers["Content-Type"] === "application/json"
    ? JSON.stringify(body)
    : body;
}

export async function get(url, customHeaders = defaultHeaders) {
  const headers = await getAuthHeaders(customHeaders);
  const res = await fetch("https://api.minibrew.io/" + url, {
    method: "GET",
    headers,
  });
  return handleResponse(res);
}

export async function post(url, body, headers = defaultHeaders) {
  // If authenticated and using default headers, check/refresh the token.
  // Otherwise (e.g. login, impersonate exchange) just use the headers as-is.
  let finalHeaders;
  if (getToken() && headers === defaultHeaders) {
    finalHeaders = await getAuthHeaders(headers);
  } else {
    finalHeaders = { ...headers };
  }
  finalHeaders = prepareHeaders(finalHeaders);

  const res = await fetch("https://api.minibrew.io/" + url, {
    method: "POST",
    headers: finalHeaders,
    body: serializeBody(finalHeaders, body),
  });
  return handleResponse(res);
}

export async function patch(url, body, headers) {
  const customHeaders = { ...defaultHeaders, ...headers };
  let finalHeaders = await getAuthHeaders(customHeaders);
  finalHeaders = prepareHeaders(finalHeaders);

  const res = await fetch("https://api.minibrew.io/" + url, {
    method: "PATCH",
    headers: finalHeaders,
    body: serializeBody(customHeaders, body),
  });
  return handleResponse(res);
}

export async function put(url, body, headers = defaultHeaders) {
  const finalHeaders = await getAuthHeaders(headers);
  const res = await fetch("https://api.minibrew.io/" + url, {
    method: "PUT",
    headers: finalHeaders,
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function del(url, headers = defaultHeaders) {
  const finalHeaders = await getAuthHeaders(headers);
  const res = await fetch("https://api.minibrew.io/" + url, {
    method: "DELETE",
    headers: finalHeaders,
  });
  return handleResponse(res);
}

// Fire-and-forget POST for pagehide: the page is dying, so no token refresh
// (getAuthHeaders awaits a network round-trip) and no response handling —
// current token as-is, keepalive so the browser finishes the send after the
// page unloads. Body must stay well under the ~64KB keepalive cap (a
// fermentation-settings payload is <1KB). Swallows every failure: this is a
// best-effort last write, the stranded-keg tile is the real recovery path.
export function postKeepalive(url, body) {
  const headers = { ...defaultHeaders, Authorization: "Bearer " + getToken() };
  return fetch("https://api.minibrew.io/" + url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    keepalive: true,
  }).then(
    () => undefined,
    () => undefined,
  );
}
